import type { DesignCanvas, DesignDocument, ImageLayer, Layer } from '../document/types.ts'
import { cloneDocument } from '../document/defaults.ts'

export type TemplateDocument = {
  id: string
  version?: number
  document: DesignDocument
}

export type UploadRef = { uploadId: string; url?: string; width?: number; height?: number }

/**
 * Vorlage auf ein Zielformat anwenden. Positionen/Größen skalieren relativ zur Trim-Box
 * (entspricht "Slots in %"), Textgrößen proportional zur Diagonale. Die Geometrie des
 * Zielprodukts (Beschnitt, Form, dpi) gewinnt immer über die der Vorlage. Läuft nur im
 * Client; der Server registriert nur templateId/templateVersion.
 */
export function applyTemplate(template: TemplateDocument, target: DesignCanvas, uploads: UploadRef[] = []): DesignDocument {
  const src = template.document
  const from = src.canvas
  const fx = target.widthMm / Math.max(1e-6, from.widthMm)
  const fy = target.heightMm / Math.max(1e-6, from.heightMm)
  const fd = Math.hypot(target.widthMm, target.heightMm) / Math.max(1e-6, Math.hypot(from.widthMm, from.heightMm))

  const layers: Layer[] = cloneDocument(src.layers).map((layer): Layer => {
    const f = layer.frame
    // Randlose Slots (bis in den Beschnitt der Vorlage) bleiben randlos im Ziel.
    const touchesLeft = f.xMm <= -from.bleedMm + 1e-6
    const touchesTop = f.yMm <= -from.bleedMm + 1e-6
    const touchesRight = f.xMm + f.wMm >= from.widthMm + from.bleedMm - 1e-6
    const touchesBottom = f.yMm + f.hMm >= from.heightMm + from.bleedMm - 1e-6
    let x = f.xMm * fx
    let y = f.yMm * fy
    let w = f.wMm * fx
    let h = f.hMm * fy
    if (layer.type !== 'image') {
      // Text/Sticker/Formen: Größe über die Diagonale, Mittelpunkt relativ
      const cx = (f.xMm + f.wMm / 2) * fx
      const cy = (f.yMm + f.hMm / 2) * fy
      w = f.wMm * fd
      h = f.hMm * fd
      x = cx - w / 2
      y = cy - h / 2
    }
    if (touchesLeft) { w += x + target.bleedMm; x = -target.bleedMm }
    if (touchesTop) { h += y + target.bleedMm; y = -target.bleedMm }
    if (touchesRight) w = target.widthMm + target.bleedMm - x
    if (touchesBottom) h = target.heightMm + target.bleedMm - y
    const frame = { ...f, xMm: r(x), yMm: r(y), wMm: r(Math.max(0, w)), hMm: r(Math.max(0, h)) }

    if (layer.type === 'text') {
      const { fittedSizePt: _drop, ...rest } = layer
      return { ...rest, frame, font: { ...layer.font, sizePt: r(layer.font.sizePt * fd) } }
    }
    if (layer.type === 'image' && layer.mask.kind === 'rounded') {
      return { ...layer, frame, mask: { kind: 'rounded', radiusMm: r(layer.mask.radiusMm * fd) } }
    }
    if (layer.type === 'shape') return { ...layer, frame, cornerRadiusMm: r(layer.cornerRadiusMm * fd) }
    return { ...layer, frame }
  })

  const doc: DesignDocument = {
    schemaVersion: 1,
    canvas: { ...target, background: src.canvas.background },
    layers,
    meta: { ...src.meta, templateId: template.id, templateVersion: template.version ?? null },
  }
  return uploads.length ? autoFill(doc, uploads) : doc
}

/** Leere Slots (placeholder) in slotIndex-Reihenfolge mit Uploads füllen. */
export function autoFill(doc: DesignDocument, uploads: UploadRef[]): DesignDocument {
  const out = cloneDocument(doc)
  const slots = out.layers
    .map((l, i) => ({ l, i }))
    .filter((e): e is { l: ImageLayer; i: number } => e.l.type === 'image' && e.l.source.kind === 'placeholder')
    .sort((a, b) => {
      const ai = (a.l.source as { slotIndex?: number }).slotIndex ?? Number.MAX_SAFE_INTEGER
      const bi = (b.l.source as { slotIndex?: number }).slotIndex ?? Number.MAX_SAFE_INTEGER
      return ai - bi || a.i - b.i
    })
  let k = 0
  for (const { l } of slots) {
    const u = uploads[k]
    if (!u) break
    k += 1
    l.source = { kind: 'upload', uploadId: u.uploadId, ...(u.url ? { url: u.url } : {}), ...(u.width ? { width: u.width } : {}), ...(u.height ? { height: u.height } : {}) }
    l.fit = { mode: 'cover', offsetX: 0.5, offsetY: 0.5, scale: 1 }
  }
  return out
}

/** Bildquellen zweier Layer tauschen (Drag & Drop zwischen Slots), Ausschnitt zurücksetzen. */
export function swapImageSources(doc: DesignDocument, aId: string, bId: string): DesignDocument {
  const out = cloneDocument(doc)
  const a = out.layers.find((l) => l.id === aId)
  const b = out.layers.find((l) => l.id === bId)
  if (!a || !b || a.type !== 'image' || b.type !== 'image') return out
  const tmp = a.source
  a.source = b.source
  b.source = tmp
  a.fit = { mode: 'cover', offsetX: 0.5, offsetY: 0.5, scale: 1 }
  b.fit = { mode: 'cover', offsetX: 0.5, offsetY: 0.5, scale: 1 }
  return out
}

function r(v: number): number {
  return Math.round(v * 100) / 100
}
